import type { TextQuestion } from '@shared/domain/form';
import { Box, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import AnimatedTick from '@shared/components/AnimatedTick';
import ErrorMessage from '@shared/components/ErrorMessage';
import { Textarea } from '@shared/components/TextArea';
import CustomTooltip from '@shared/components/Tooltip';
import { QuestionCard } from '../../styles/review.styles';
import {
  BaseQuestionText,
  Counter,
  QuestionHeaderRow,
  QuestionHelperText,
  RequiredBadge,
} from './shared/QuestionStyles';

interface ReviewTextAreaProps {
  question: TextQuestion;
  formFields: Record<string, any>;
  handleFormFieldChange: (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => void;
  errors: Record<string, string>;
  includeTextLimitValidation?: boolean;
  helperText?: string;
  index?: number;
  short?: boolean;
}

// Using shared components from QuestionStyles
export const HelperText = QuestionHelperText;
const HeaderRow = QuestionHeaderRow;
const QuestionText = BaseQuestionText;

const CHARACTER_MIN_LIMIT = 50;

const ReviewTextArea: React.FC<ReviewTextAreaProps> = ({
  question,
  formFields,
  handleFormFieldChange,
  errors,
  includeTextLimitValidation,
  helperText,
  short,
  index = 0,
}) => {
  const [remainingCharacters, setRemainingCharacters] = useState(
    Math.max(CHARACTER_MIN_LIMIT - (formFields[question.name]?.length || 0), 0),
  );

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;
    setRemainingCharacters(Math.max(CHARACTER_MIN_LIMIT - text.length, 0));
    handleFormFieldChange(event);
  };

  const isBelowMinimum
    = (formFields[question.name]?.length || 0) < CHARACTER_MIN_LIMIT;

  return (
    <Grid size={{ sm: 6, xs: 12 }} key={question.name}>
      <motion.div
        {...(question.conditionallyShow
          ? {
              initial: {
                x: '-100%',
                opacity: 0,
              },
              transition: {
                type: 'spring',
                stiffness: 100,
                damping: 15,
                delay: index * 0.2,
              },
            }
          : {})}
        animate={{
          x: 0,
          opacity: 1,
        }}
      >
        <QuestionCard data-testid={question.name}>
          <HeaderRow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <QuestionText id={`${question.name}-label`} as="div">
                {question.question}
                {question.required && <RequiredBadge>Required</RequiredBadge>}
              </QuestionText>
              {question.tooltipText && (
                <CustomTooltip title={question.tooltipText} />
              )}
            </Box>
            {includeTextLimitValidation && (
              <Counter $warn={isBelowMinimum} aria-live="polite">
                {isBelowMinimum
                  ? (
                      `${remainingCharacters} more characters needed`
                    )
                  : (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span>Minimum met</span>
                        <AnimatedTick />
                      </Box>
                    )}
              </Counter>
            )}
          </HeaderRow>

          <Textarea
            aria-labelledby={`${question.name}-label`}
            aria-invalid={!!errors[question.name]}
            aria-describedby={
              helperText || errors[question.name]
                ? `${question.name}-desc`
                : undefined
            }
            onChange={handleTextChange}
            name={question.name}
            value={formFields[question.name] || ''}
            minRows={4}
            required={question.required}
            style={{
              width: '100%',
              resize: 'none',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '12px 14px',
              fontSize: '14px',
              lineHeight: 1.5,
              minHeight: 'calc(var(--textarea-rows) * 1em * var(--textarea-line-height) + 24px)',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
            }}
          />

          {helperText && (
            <HelperText id={`${question.name}-desc`} $short={short}>
              {helperText}
            </HelperText>
          )}

          {errors[question.name] && (
            <ErrorMessage error={errors[question.name]} />
          )}
        </QuestionCard>
      </motion.div>
    </Grid>
  );
};

export default ReviewTextArea;
